import React from "react";
import { asyncLoadScript } from "../utils/common";
import { APP_CONFIG } from "../constants/constants";
import { useDispatch, useSelector } from "react-redux";
import {
  setMapStateData,
  setMapZoomForControlsData,
} from "../api/store/commonSlice";
import { TMapState, TMapType } from "../types/map";
import { getDefaultMapLayerOptions } from "../components/maps/basicMap";
import { TMapRef, TMapRefGeozones } from "../pages/map-fleet/type";
import { TMapRef as TMapRefVehicle } from "../pages/map-asset/type";
import { useGMapMode } from "./mapGoogle";
import { useLoggedInUserData } from "../utils/user";
import { GOOGLEMAPS_ENDPOINTS } from "../api/network/endpoints";
import { useDebouncedCallback } from "use-debounce";

export const useMapInit = () => {
  const initRef = React.useRef(false);
  const dispatch = useDispatch();

  const userMapDefault = useLoggedInUserData("mapDefault");

  // if (APP_CONFIG.DEBUG.MAPS) console.log("userMapDefault", userMapDefault);

  const getUserPreferredMapType = (mapDefault: string | null): TMapType => {
    if (!mapDefault) {
      return APP_CONFIG.MAPS.DEFAULTS.TYPE as TMapType;
    }

    const preference = parseInt(mapDefault);

    switch (preference) {
      case 0:
        return "roadmap" as TMapType;
      case 1:
        return "satellite" as TMapType;
      default:
        return APP_CONFIG.MAPS.DEFAULTS.TYPE as TMapType;
    }
  };

  const mapState = useSelector(
    (state: { commonReducer: { mapState: TMapState } }) =>
      state.commonReducer.mapState
  );
  const mapZoomForControls = useSelector(
    (state: { commonReducer: { mapZoomForControls: number } }) =>
      state.commonReducer.mapZoomForControls
  );

  const { gMapGetMode, gMapGetZoom } = useGMapMode();

  const initMap = () => {
    // initialize the map
    if (!initRef.current) {
      initRef.current = true;

      // load map script
      const loadMapScript = () => {
        asyncLoadScript(
          APP_CONFIG.MAPS.SCRIPT_URL(
            `${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
          ),
          APP_CONFIG.MAPS.SCRIPT_ID,
          () => {
            if (APP_CONFIG.DEBUG.MAPS) console.log("Map script loaded");
            setTimeout(() => {
              // load map modules
              // const google = (window as any).google;
              // google?.maps?.loadModule(
              //   [
              //     "Microsoft.Maps.SpatialMath",
              //     "Microsoft.Maps.Contour",
              //     "Microsoft.Maps.Clustering",
              //   ],
              //   () => {
              //     // set map script loaded
              //     dispatch(
              //       setMapStateData({
              //         mapScriptLoaded: true,
              //         mapType: APP_CONFIG.MAPS.DEFAULTS.TYPE as TMapType,
              //         mapLayerOptions: getDefaultMapLayerOptions(mapState),
              //       })
              //     );
              //   }
              // );
              const preferredMapType = getUserPreferredMapType(userMapDefault);

              if (APP_CONFIG.DEBUG.MAPS)
                console.log("preferredMapType", preferredMapType);

              dispatch(
                setMapStateData({
                  mapScriptLoaded: true,
                  mapType: preferredMapType,
                  mapLayerOptions: getDefaultMapLayerOptions(mapState),
                })
              );
              // TODO: Pre load user current position
            }, /*loadingMap ?*/ 2000 /* : 200*/); // delay necessary to let map resources load, before attempting to load the map
          }
        );
      };

      // 7/3/2024: we let the map load directly, as we currently don't need user's current position
      loadMapScript();

      // disabled 7/3/2024, as we currently don't need user's current position
      // mapGetCurrentPosition((currPos: TLatLng) => {
      //   // set user current position in store
      //   dispatch(setUserCurrPosData(currPos));
      //   // load map script
      //   loadMapScript();
      // });
    }
  };

  const originalGIdentifyAndSetMapModeToStore = (map: google.maps.Map) => {
    const newMapMode = gMapGetMode(map);
    if (APP_CONFIG.DEBUG.MAPS)
      console.log(
        `[gIdentifyAndSet...] OLD: ${mapState.mapType} NEW: ${newMapMode}`
      );
    setTimeout(() => {
      // update store with new map mode
      // if condition added to prevent un-necessary update to store data (which was causing issues with edit circle geozone blue marker changes getting reset)
      // 11/13/2025 disengaging condition and wrapping in setTimeout to allow any ongoing map mode transitions to complete
      // if (newMapMode !== mapState.mapType) {
      dispatch(
        setMapStateData({
          // ...mapState,
          mapType: newMapMode,
          pageMapLoaded: true,
        } as TMapState)
      );
      // }
    }, 200); // 500
    return newMapMode;
  };

  // debounded
  // const gIdentifyAndSetMapModeToStore = useDebouncedCallback(
  //   originalGIdentifyAndSetMapModeToStore,
  //   500
  // );
  const gIdentifyAndSetMapModeToStore = originalGIdentifyAndSetMapModeToStore;

  const gUpdateMapZoomLevelToStore = (map: google.maps.Map) => {
    const newMapZoom = gMapGetZoom(map);
    // using mapZoomForControls instead of mapState.mapZoom for comparison
    if (newMapZoom !== mapZoomForControls) {
      dispatch(setMapZoomForControlsData(newMapZoom));
    }
    return newMapZoom;
  };

  const onGAutocompletePlaceChanged = (
    place: google.maps.places.PlaceResult,
    // mapRef:
    //   | React.RefObject<TMapRefVehicle>
    //   | React.RefObject<TMapRef>
    //   | React.RefObject<TMapRefGeozones>,
    mapRef: React.RefObject<{ map: any; searchMarker?: any }>,
    onFinishCallback?: (place: google.maps.LatLng | undefined) => void
  ) => {
    if (APP_CONFIG.DEBUG.MAPS) console.log("onAutocompletePlaceChanged", place);
    const geocodeResponse = place?.geometry?.location;
    if (APP_CONFIG.DEBUG.MAPS) console.log("geocodeResponse", geocodeResponse);
    const newlySelectedLocationCoords = [
      geocodeResponse?.lat(),
      geocodeResponse?.lng(),
    ];
    if (APP_CONFIG.DEBUG.MAPS)
      console.log("newlySelectedLocationCoords", newlySelectedLocationCoords);
    if (
      !!newlySelectedLocationCoords?.[0] &&
      !!newlySelectedLocationCoords?.[1]
    ) {
      // use viewport to zoom to the selected location - more efficient
      mapRef.current?.map?.fitBounds(
        place?.geometry?.viewport
          ? {
              east: place?.geometry?.viewport?.getNorthEast()?.lng(),
              north: place?.geometry?.viewport?.getNorthEast()?.lat(),
              south: place?.geometry?.viewport?.getSouthWest()?.lat(),
              west: place?.geometry?.viewport?.getSouthWest()?.lng(),
            }
          : {
              east: (place?.geometry?.location as any)?.lng() + 0.01,
              north: (place?.geometry?.location as any)?.lat() + 0.01,
              south: (place?.geometry?.location as any)?.lat() - 0.01,
              west: (place?.geometry?.location as any)?.lng() - 0.01,
            }
      );

      if (mapRef.current?.searchMarker) {
        mapRef.current.searchMarker.setMap(null);
      }

      const marker = new google.maps.Marker({
        position: geocodeResponse,
        map: mapRef.current?.map,
        title: place.name || place.formatted_address || "Selected Location",
        animation: google.maps.Animation.DROP,
      });

      if (mapRef.current) {
        mapRef.current.searchMarker = marker;
      }

      const btnId = `streetview-btn-${place.name?.replace(/\s+/g, "-")}`;

      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="padding: 8px;">
            ${place.name ? `<strong>${place.name}</strong><br/>` : ""}
            ${place.formatted_address || ""}
            <button 
              id="${btnId}"
              class="flex items-center justify-center group h-10 w-full px-4 rounded-lg border text-sm font-display font-semibold transition border-accent-blue-pale bg-accent-blue-pale text-accent-blue-dark enabled:hover:bg-accent-blue-dark enabled:hover:text-white"
              style="margin-top: 8px;"
            >
              Open Street View
            </button>
          </div>
        `,
      });

      const getDefaultStreetViewData = async (
        address: string
      ): Promise<{
        panoId: string;
        heading: number;
        location: google.maps.LatLng;
      } | null> => {
        // I didn't use the endpoints file because it is using lat lng there and this call can not use lat lng
        const url = `https://maps.googleapis.com/maps/api/streetview/metadata?location=${encodeURIComponent(address)}&source=outdoor&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
        try {
          const response = await fetch(url);
          const data = await response.json();

          if (data.status === "OK" && data.pano_id) {
            const panoLocation = new google.maps.LatLng(
              data.location.lat,
              data.location.lng
            );

            const geocoder = new google.maps.Geocoder();
            const geocodeResult =
              await new Promise<google.maps.GeocoderResult | null>(
                (resolve) => {
                  geocoder.geocode({ address: address }, (results, status) => {
                    if (status === "OK" && results && results[0]) {
                      resolve(results[0]);
                    } else {
                      resolve(null);
                    }
                  });
                }
              );

            if (geocodeResult) {
              const targetLocation = geocodeResult.geometry.location;
              const heading = google.maps.geometry.spherical.computeHeading(
                panoLocation,
                targetLocation
              );

              return {
                panoId: data.pano_id,
                heading: heading,
                location: panoLocation,
              };
            }
          }
        } catch (error) {
          console.error("Failed to get Street View metadata:", error);
        }

        return null;
      };

      marker.addListener("click", () => {
        infoWindow.open(mapRef.current?.map, marker);

        google.maps.event.addListenerOnce(infoWindow, "domready", () => {
          const btn = document.getElementById(btnId);
          if (btn && geocodeResponse) {
            btn.addEventListener("click", async () => {
              try {
                const svData = await getDefaultStreetViewData(
                  place.formatted_address ||
                    `${geocodeResponse.lat()},${geocodeResponse.lng()}`
                );

                if (svData) {
                  const panorama = new google.maps.StreetViewPanorama(
                    mapRef.current?.map.getDiv(),
                    {
                      pano: svData.panoId,
                      pov: {
                        heading: svData.heading,
                        pitch: 0,
                      },
                      zoom: 1,
                      enableCloseButton: true,
                    }
                  );

                  mapRef.current?.map.setStreetView(panorama);
                  panorama.setVisible(true);

                  // sync map mode to store after a short delay
                  setTimeout(() => {
                    gIdentifyAndSetMapModeToStore(mapRef.current!.map);
                  }, 200);

                  if (mapRef.current?.searchMarker) {
                    mapRef.current.searchMarker.setMap(null);
                  }

                  google.maps.event.addListener(
                    panorama,
                    "visible_changed",
                    () => {
                      if (!panorama.getVisible()) {
                        if (mapRef.current?.searchMarker) {
                          mapRef.current.searchMarker.setMap(
                            mapRef.current?.map
                          );
                        }
                        mapRef.current?.map.setStreetView(null);

                        // sync map mode to store after a short delay
                        setTimeout(() => {
                          gIdentifyAndSetMapModeToStore(mapRef.current!.map);
                        }, 200);
                      }
                    }
                  );
                } else {
                  console.error(
                    "Street View data not found for this location."
                  );
                }
              } catch (error) {
                console.error("Street View error:", error);
              }
            });
          }
        });
      });

      onFinishCallback?.(
        !!geocodeResponse?.lat() && !!geocodeResponse?.lng()
          ? new google.maps.LatLng(
              geocodeResponse?.lat(),
              geocodeResponse?.lng()
            )
          : undefined
      );
    } else {
      if (APP_CONFIG.DEBUG.MAPS)
        console.error("GEOCODE RESPONSE ISSUE - no coordinates");
    }
  };

  return {
    initMap,
    gIdentifyAndSetMapModeToStore,
    gUpdateMapZoomLevelToStore,
    onGAutocompletePlaceChanged,
  };
};
